import React from "react";
import { PortfolioItem } from "@/hooks/useProfile";
import { CaseStudyItem } from "@/types/case-study";
import { UnifiedPortfolioItem as DatabaseUnifiedPortfolioItem } from "@/services/supabase/unifiedPortfolio";
import { UnifiedPortfolioItem } from "@/types/unified-portfolio";
import PortfolioItemsList from "./components/PortfolioItemsList";
import { CaseStudyList } from "./case-study/CaseStudyList";
import UnifiedPortfolioList from "./UnifiedPortfolioList";
import { useUnifiedPortfolio } from "@/hooks/portfolio/useUnifiedPortfolio";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CombinedPortfolioListProps {
  portfolioItems: PortfolioItem[];
  caseStudies: CaseStudyItem[];
  caseStudiesLoading: boolean;
  onRemove: (index: number) => void;
  onEdit: (id: string) => void;
  onRename: (id: string, newTitle: string) => void;
  onReorder: (items: PortfolioItem[]) => void;
  onSetFeatured: (id: string) => void;
  onUpdateStatus: (id: string, status: string) => void;
  onEditCaseStudy: (caseStudy: CaseStudyItem) => void;
  onPreviewCaseStudy: (caseStudy: CaseStudyItem) => void;
  onCreateNewCaseStudy: () => void;
  onPortfolioChange: (items: PortfolioItem[]) => void;
  onCaseStudyChange?: (items: CaseStudyItem[]) => void;
  isProcessing: boolean;
  useUnifiedView?: boolean; // New prop to enable unified view
  // New props for unified data source
  unifiedItems?: UnifiedPortfolioItem[];
  useUnifiedSource?: boolean;
  sortOrder?: string;
}

export const CombinedPortfolioList: React.FC<CombinedPortfolioListProps> = ({
  portfolioItems,
  caseStudies,
  caseStudiesLoading,
  onRemove,
  onEdit,
  onRename,
  onReorder,
  onSetFeatured,
  onUpdateStatus,
  onEditCaseStudy,
  onPreviewCaseStudy,
  onCreateNewCaseStudy,
  onPortfolioChange,
  onCaseStudyChange,
  isProcessing,
  useUnifiedView = false,
  unifiedItems = [],
  useUnifiedSource = false,
  sortOrder = 'last_added',
}) => {
  const queryClient = useQueryClient();
  const singleUnifiedAutoFeatureRef = React.useRef<string | null>(null);
  const singleShotAutoFeatureRef = React.useRef<string | null>(null);
  // Choose data source based on unified source flag
  const currentPortfolioItems = useUnifiedSource ? 
    unifiedItems.filter(item => item.item_type === 'shot').map(item => ({
      id: item.id,
      title: item.title,
      description: item.description,
      image_url: item.image_url || '',
      images: item.images || [],
      created_at: item.created_at,
      updated_at: item.updated_at,
      is_featured: item.is_featured,
      status: item.status,
      order: item.order,
      slug: item.slug
    })) : 
    portfolioItems;
  const currentCaseStudies = useUnifiedSource ? 
    unifiedItems.filter(item => item.item_type === 'case_study').map(item => ({
      id: item.id,
      title: item.title,
      description: item.description,
      cover_image: item.image_url || '',
      status: item.status,
      is_featured: item.is_featured,
      slug: item.slug,
      seo_title: item.seo_title,
      seo_description: item.seo_description,
      og_image: item.og_image,
      order_index: item.order || 0,
      created_at: item.created_at,
      updated_at: item.updated_at,
      profile_id: item.profile_id,
      sections: [] // Will be populated separately if needed
    })) : 
    caseStudies;
  const currentLoading = useUnifiedSource ? false : caseStudiesLoading; // Unified source has single loading state

  // Unified portfolio hook - only use when not using unified source
  const {
    unifiedItems: hookUnifiedItems,
    isProcessing: unifiedProcessing,
  } = useUnifiedPortfolio({
    portfolioItems: useUnifiedSource ? [] : currentPortfolioItems,
    caseStudies: useUnifiedSource ? [] : currentCaseStudies,
    onPortfolioChange,
    onCaseStudyChange,
  });

  // Use the passed unifiedItems when useUnifiedSource is true
  const finalUnifiedItems = useUnifiedSource ? unifiedItems : hookUnifiedItems;

  // Unified handlers that work with direct database updates
  const handleUnifiedUpdateStatus = async (id: string, status: "draft" | "published") => {
    const item = finalUnifiedItems.find(i => i.id === id);
    if (!item) return;

    try {
      if (item.type === "shot") {
        const { PortfolioService } = await import("@/services/supabase/portfolio");
        const updates: any = { status };
        if (status === "draft") {
          updates.is_featured = false;
        }
        await PortfolioService.updatePortfolioItem(id, updates);
      } else if (item.type === "case_study") {
        const { CaseStudyService } = await import("@/services/supabase/caseStudies");
        await CaseStudyService.updateCaseStudy({
          id,
          status,
          ...(status === "draft" ? { is_featured: false } : {}),
        });
      }
      
      // Invalidate queries to refresh the data
      await queryClient.invalidateQueries({ queryKey: ['unified-portfolio'] });
      await queryClient.invalidateQueries({ queryKey: ['case-studies'] });
      await queryClient.invalidateQueries({ queryKey: ['unified-case-studies'] });
      const message =
        status === "draft"
          ? item.type === "shot"
            ? "Shot saved as draft successfully"
            : "Case study saved as draft successfully"
          : item.type === "shot"
            ? "Shot published successfully"
            : "Case study published successfully";
      toast.success(message);
    } catch (error) {
      console.error('Error updating item status:', error);
      toast.error("Failed to update item status");
    }
  };

  const handleUnifiedSetFeatured = async (id: string) => {
    const item = finalUnifiedItems.find(i => i.id === id);
    if (!item) return;

    try {
      // Step 1: Remove featured status from ALL items (both shots and case studies)
      const allFeaturedItems = finalUnifiedItems.filter(i => i.is_featured && i.id !== id);
      
      for (const featuredItem of allFeaturedItems) {
        if (featuredItem.type === "shot") {
          const { PortfolioService } = await import("@/services/supabase/portfolio");
          await PortfolioService.updatePortfolioItem(featuredItem.id, { is_featured: false });
        } else if (featuredItem.type === "case_study") {
          const { CaseStudyService } = await import("@/services/supabase/caseStudies");
          await CaseStudyService.updateCaseStudy({ id: featuredItem.id, is_featured: false });
        }
      }

      // Step 2: Set the selected item as featured (or remove if already featured)
      const newFeaturedStatus = !item.is_featured;
      if (item.type === "shot") {
        const { PortfolioService } = await import("@/services/supabase/portfolio");
        await PortfolioService.updatePortfolioItem(id, { is_featured: newFeaturedStatus });
      } else if (item.type === "case_study") {
        const { CaseStudyService } = await import("@/services/supabase/caseStudies");
        await CaseStudyService.updateCaseStudy({ id, is_featured: newFeaturedStatus });
      }

      // Step 3: Refresh UI
      await queryClient.invalidateQueries({ queryKey: ['unified-portfolio'] });
      await queryClient.invalidateQueries({ queryKey: ['case-studies'] });
      await queryClient.invalidateQueries({ queryKey: ['unified-case-studies'] });
      
      toast.success(newFeaturedStatus ? 'Set as featured' : 'Removed from featured');
    } catch (error) {
      console.error('Error updating featured status:', error);
      toast.error("Failed to update featured status");
    }
  };

  React.useEffect(() => {
    if (!useUnifiedView) {
      singleUnifiedAutoFeatureRef.current = null;
      return;
    }

    if (finalUnifiedItems.length !== 1) {
      singleUnifiedAutoFeatureRef.current = null;
      return;
    }

    const singleItem = finalUnifiedItems[0];
    if (singleItem.status !== "published") {
      singleUnifiedAutoFeatureRef.current = null;
      return;
    }

    if (singleItem.is_featured || singleUnifiedAutoFeatureRef.current === singleItem.id) {
      return;
    }

    singleUnifiedAutoFeatureRef.current = singleItem.id;
    handleUnifiedSetFeatured(singleItem.id).catch((error) => {
      console.error("Error auto featuring unified item:", error);
      singleUnifiedAutoFeatureRef.current = null;
    });
  }, [useUnifiedView, finalUnifiedItems, handleUnifiedSetFeatured]);

  React.useEffect(() => {
    if (useUnifiedView || !onSetFeatured) {
      singleShotAutoFeatureRef.current = null;
      return;
    }

    if (currentPortfolioItems.length !== 1) {
      singleShotAutoFeatureRef.current = null;
      return;
    }

    const singleShot = currentPortfolioItems[0];
    if (singleShot.status !== "published") {
      singleShotAutoFeatureRef.current = null;
      return;
    }

    if (singleShot.is_featured || singleShotAutoFeatureRef.current === singleShot.id) {
      return;
    }

    singleShotAutoFeatureRef.current = singleShot.id;
    onSetFeatured(singleShot.id);
  }, [useUnifiedView, currentPortfolioItems, onSetFeatured]);

  const handleUnifiedRename = async (id: string, newTitle: string) => {
    const item = finalUnifiedItems.find(i => i.id === id);
    if (!item) return;

    try {
      if (item.type === "shot") {
        const { PortfolioService } = await import("@/services/supabase/portfolio");
        await PortfolioService.updatePortfolioItem(id, { title: newTitle });
      } else if (item.type === "case_study") {
        const { CaseStudyService } = await import("@/services/supabase/caseStudies");
        await CaseStudyService.updateCaseStudy({ id, title: newTitle });
      }
      
      // Invalidate queries to refresh the data
      await queryClient.invalidateQueries({ queryKey: ['unified-portfolio'] });
      await queryClient.invalidateQueries({ queryKey: ['case-studies'] });
      await queryClient.invalidateQueries({ queryKey: ['unified-case-studies'] });
      
      toast.success("Item renamed successfully");
    } catch (error) {
      console.error('Error renaming item:', error);
      toast.error("Failed to rename item");
    }
  };

  const handleUnifiedDelete = async (id: string) => {
    const item = finalUnifiedItems.find(i => i.id === id);
    if (!item) return;

    try {
      if (item.type === "shot") {
        const { PortfolioService } = await import("@/services/supabase/portfolio");
        await PortfolioService.deletePortfolioItem(id);
      } else if (item.type === "case_study") {
        const { CaseStudyService } = await import("@/services/supabase/caseStudies");
        await CaseStudyService.deleteCaseStudy(id);
      }

      // Invalidate queries to refresh the data
      await queryClient.invalidateQueries({ queryKey: ['unified-portfolio'] });
      await queryClient.invalidateQueries({ queryKey: ['case-studies'] });
      await queryClient.invalidateQueries({ queryKey: ['unified-case-studies'] });

      toast.success("Item deleted successfully");
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error("Failed to delete item");
    }
  };

  const handleUnifiedDuplicate = async (id: string) => {
    const item = finalUnifiedItems.find(i => i.id === id);
    if (!item) return;

    try {
      if (item.type === "shot") {
        const { PortfolioService } = await import("@/services/supabase/portfolio");
        await PortfolioService.duplicatePortfolioItem(id);

        // Invalidate queries to refresh the data
        await queryClient.invalidateQueries({ queryKey: ['unified-portfolio'] });
        await queryClient.invalidateQueries({ queryKey: ['case-studies'] });
        await queryClient.invalidateQueries({ queryKey: ['unified-case-studies'] });

        toast.success("Shot duplicated successfully");
      } else if (item.type === "case_study") {
        const { CaseStudyService } = await import("@/services/supabase/caseStudies");
        await CaseStudyService.duplicateCaseStudy(id);

        // Invalidate queries to refresh the data
        await queryClient.invalidateQueries({ queryKey: ['unified-portfolio'] });
        await queryClient.invalidateQueries({ queryKey: ['case-studies'] });
        await queryClient.invalidateQueries({ queryKey: ['unified-case-studies'] });

        toast.success("Case study duplicated successfully");
      }
    } catch (error) {
      console.error('Error duplicating item:', error);
      toast.error("Failed to duplicate item");
    }
  };

  // Use the appropriate data source for empty state check
  const hasPortfolioItems = useUnifiedSource ? 
    (unifiedItems?.filter(item => item.type === 'shot').length || 0) > 0 : 
    hookUnifiedItems.filter(item => item.type === 'shot').length > 0;
  const hasCaseStudies = useUnifiedSource ? 
    (unifiedItems?.filter(item => item.type === 'case_study').length || 0) > 0 : 
    hookUnifiedItems.filter(item => item.type === 'case_study').length > 0;





  // If unified view is enabled, show the unified portfolio list
  if (useUnifiedView) {
    return (
      <div className="space-y-8">
        {/* Unified Portfolio View - Always show, but with loading state for case studies */}
        <div className="md:px-0">
          {/* Show loading state only if we have no data at all and loading */}
          {currentLoading && finalUnifiedItems.length === 0 ? (
            <div className="space-y-4">
              <div className="animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      className="aspect-square bg-gray-200 rounded-lg"
                    ></div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <UnifiedPortfolioList
              items={finalUnifiedItems}
              onRemove={handleUnifiedDelete}
              onEdit={(id) => {
                const item = finalUnifiedItems.find(i => i.id === id);
                if (item?.type === "shot") {
                  onEdit(id);
                } else if (item?.type === "case_study") {
                  // Convert UnifiedPortfolioItem to CaseStudyItem format
                  const caseStudy = {
                    id: item.id,
                    title: item.title,
                    description: item.description,
                    seo_title: item.seo_title || '',
                    seo_description: item.seo_description || '',
                    og_image: item.og_image || '',
                    status: item.status,
                    is_featured: item.is_featured,
                    order: item.order,
                    created_at: item.created_at,
                    updated_at: item.updated_at,
                    slug: item.slug,
                  };
                  onEditCaseStudy(caseStudy);
                }
              }}
              onRename={handleUnifiedRename}
              onSetFeatured={handleUnifiedSetFeatured}
              onUpdateStatus={handleUnifiedUpdateStatus}
              onDuplicate={handleUnifiedDuplicate}
              sortOrder={sortOrder}
              onPreview={(item) => {
                if (item.type === "case_study") {
                  // Convert UnifiedPortfolioItem to CaseStudyItem format
                  const caseStudy = {
                    id: item.id,
                    title: item.title,
                    description: item.description,
                    seo_title: item.seo_title || '',
                    seo_description: item.seo_description || '',
                    og_image: item.og_image || '',
                    status: item.status,
                    is_featured: item.is_featured,
                    order: item.order,
                    created_at: item.created_at,
                    updated_at: item.updated_at,
                    slug: item.slug,
                  };
                  onPreviewCaseStudy(caseStudy);
                }
              }}
              isProcessing={unifiedProcessing}
            />
          )}
          
        </div>

        {/* No items message - only show when there are no items at all */}
        {!hasPortfolioItems && !hasCaseStudies && !caseStudiesLoading && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">No Portfolio Items Yet</h3>
            <p className="text-gray-600 mb-4">
              Create your first portfolio item or case study to showcase your work.
            </p>
          </div>
        )}
      </div>
    );
  }

  // Original separate view
  return (
    <div className="space-y-8">
      {/* Portfolio Items (Shots) */}
      {hasPortfolioItems && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Shots</h3>
          <PortfolioItemsList
            items={currentPortfolioItems}
            onRemove={onRemove}
            onEdit={onEdit}
            onRename={onRename}
            isProcessing={isProcessing}
            onReorder={onReorder}
            onSetFeatured={onSetFeatured}
            onUpdateStatus={onUpdateStatus}
          />
        </div>
      )}

      {/* Case Studies */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Case Studies</h3>
        <CaseStudyList
          onEdit={onEditCaseStudy}
          onPreview={onPreviewCaseStudy}
          onCreateNew={onCreateNewCaseStudy}
        />
      </div>

      {/* No portfolio items message - only show when there are no portfolio items AND no case studies */}
      {!hasPortfolioItems && !hasCaseStudies && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2">No Portfolio Items Yet</h3>
          <p className="text-gray-600 mb-4">
            Upload your first portfolio item to showcase your work.
          </p>
        </div>
      )}
    </div>
  );
};

export default React.memo(CombinedPortfolioList);
